import * as React from "react";
import { ContactModal } from "../components/ContactModal";
import { Footer } from "../components/Footer";
import { Header } from "../components/Header";
import { useContactModal } from "../context/contactModal";
import { COMPANY } from "../data/company";
export const LegalDocument = ({ title, content }) => {
  const { isOpen, closeModal } = useContactModal();
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header forceLight={true} />
      <main className="flex-1 max-w-4xl mx-auto px-4 pt-24 pb-16 w-full">
        <section className="prose prose-lg">
          <h1 className="text-3xl font-bold text-black mb-2">{title}</h1>
          {content}
        </section>
      </main>
      <Footer />
      <ContactModal isOpen={isOpen} onClose={closeModal} />
    </div>
  );
};
export const PrivacyPage = () => (
  <LegalDocument
    title="Privacy Policy"
    content={
      <React.Fragment>
        <section className="mb-12">
          <h2 className="text-xl font-bold text-slate-900 mb-4 border-b pb-2">I. Data Philosophy</h2>
          <p className="text-gray-600 leading-relaxed">
            At Actrium AI, we believe Physical AI requires the highest standards of data integrity. Our
            privacy framework is designed to protect the multi-modal interaction streams captured through the{" "}
            <strong>AEgo</strong> capture ecosystem, ensuring that human experience is translated into machine
            intelligence without compromising individual privacy.
          </p>
        </section>
        <section className="mb-12">
          <h2 className="text-xl font-bold text-slate-900 mb-4 border-b pb-2">
            II. Multi-modal Data Capture
          </h2>
          <p className="text-gray-600 leading-relaxed mb-4">
            Our sensors capture high-fidelity streams including egocentric vision, inertial measurement unit
            (IMU) data, and muscle/force dynamics (FFMG). This data is used exclusively for:
          </p>
          <ul className="list-disc ml-6 space-y-2 text-base text-gray-600">
            <li>
              <strong>Robot-Ready Data Conversion</strong>: Mapping human trajectories to robotic embodiments.
            </li>
            <li>
              <strong>Skill Model Training</strong>: Developing foundational models for contact-rich
              manipulation.
            </li>
            <li>
              <strong>System Optimization</strong>: Improving the latency and accuracy of AEgo Engine and
              EGO-to-Robot workflows.
            </li>
          </ul>
        </section>
        <section className="mb-12">
          <h2 className="text-xl font-bold text-slate-900 mb-4 border-b pb-2">III. Security Architecture</h2>
          <p className="text-gray-600 leading-relaxed">
            All datasets are stored using industry-standard AES-256 encryption. We utilize hardware-level
            anonymization to strip identifiable features from vision streams before the action-tokenization
            process begins.
          </p>
        </section>
        <section className="mb-12">
          <h2 className="text-xl font-bold text-slate-900 mb-4 border-b pb-2">IV. U.S. Data Residency</h2>
          <p className="text-gray-600 leading-relaxed">
            Actrium AI is a U.S.-based company. Customer data, including human demonstration data, robot
            execution data, deployment feedback, and associated metadata, is stored and processed in the
            United States by default unless a separate written agreement states otherwise.
          </p>
          <p className="text-gray-600 leading-relaxed mt-4">
            For privacy-sensitive projects, Actrium workflows are designed around U.S. data residency,
            privacy-aware processing, controlled access, and limited use of raw video or personally
            identifiable information.
          </p>
        </section>
        <section>
          <h2 className="text-xl font-bold text-slate-900 mb-4">Contact</h2>
          <p className="text-gray-600 leading-relaxed">
            {COMPANY.legalName}
            <br />
            {COMPANY.addressLine1}
            <br />
            {COMPANY.addressLine2}
          </p>
          <p className="mt-4 text-[#006AFF]">{COMPANY.email}</p>
        </section>
      </React.Fragment>
    }
  />
);
export const TermsPage = () => (
  <LegalDocument
    title="Terms of Service"
    content={
      <React.Fragment>
        <section className="mb-12">
          <h2 className="text-xl font-bold text-slate-900 mb-4 border-b pb-2">
            I. Proprietary Infrastructure
          </h2>
          <p className="text-gray-600 leading-relaxed">
            All components of the Actrium AI ecosystem—including the <strong>AEgo Engine</strong>,{" "}
            <strong>NeuroScale</strong> representations,<strong> EGO-to-Robot</strong> conversion workflows,
            and <strong>AEgo</strong> sensor designs—constitute proprietary trade secrets and intellectual
            property. Access to our developer portal and datasets is granted via specific enterprise licenses.
          </p>
        </section>
        <section className="mb-12">
          <h2 className="text-xl font-bold text-slate-900 mb-4 border-b pb-2">II. Usage Compliance</h2>
          <p className="text-gray-600 leading-relaxed mb-4">
            Users, partners, and licensees agree to the following restrictions:
          </p>
          <ul className="list-disc ml-6 space-y-2 text-base text-gray-600">
            <li>
              <strong>No Reverse Engineering</strong>: Attempting to deconstruct our tactile sensing
              algorithms or compilation logic is prohibited.
            </li>
            <li>
              <strong>Data Integrity</strong>: Users may not inject synthetic or corrupted data into the
              Actrium training pipeline without authorization.
            </li>
            <li>
              <strong>Attribution</strong>: Any research or product utilizing Actrium-generated datasets must
              provide appropriate technical attribution.
            </li>
          </ul>
        </section>
        <section className="mb-12">
          <h2 className="text-xl font-bold text-slate-900 mb-4 border-b pb-2">III. U.S. Data Residency</h2>
          <p className="text-gray-600 leading-relaxed">
            Actrium AI is a U.S.-based company. Unless otherwise agreed in writing, customer data submitted
            to, generated through, or processed by Actrium services is stored and processed in the United
            States.
          </p>
          <p className="text-gray-600 leading-relaxed mt-4">
            This includes human demonstration data, robot execution data, deployment feedback, metadata, and
            model-evaluation artifacts associated with Actrium workflows. Any alternative data residency
            arrangement must be governed by a separate written agreement.
          </p>
        </section>
        <section className="mb-12">
          <h2 className="text-xl font-bold text-slate-900 mb-4 border-b pb-2">IV. Limitation of Liability</h2>
          <p className="text-gray-600 leading-relaxed">
            Actrium AI provides foundational data infrastructure for Physical AI. However, we are not liable
            for the real-world performance of third-party robotic systems or any damages resulting from models
            trained on our datasets in unverified environments.
          </p>
        </section>
        <section>
          <h2 className="text-xl font-bold text-slate-900 mb-4">Contact</h2>
          <p className="text-gray-600 leading-relaxed">
            {COMPANY.legalName}
            <br />
            {COMPANY.addressLine1}
            <br />
            {COMPANY.addressLine2}
          </p>
          <p className="mt-4 text-[#006AFF]">{COMPANY.email}</p>
        </section>
      </React.Fragment>
    }
  />
);
